import { Link, useNavigate } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader } from "@/components/ui/loader";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";
import { BackButton } from "@/components/ui/BackButton";

type UserRecord = {
  id: string;
  englishName: string | null;
  scientificName: string | null;
  orgName: string | null;
  orgId: string | null;
  dtm: string;
  count: number | null;
  lat: number | null;
  lng: number | null;
  note: string | null;
  locationNote: string | null;
  createdAt: string;
  updatedAt: string;
};

const fetchUserRecords = async (): Promise<UserRecord[]> => {
  const res = await fetch("/api/me/records");
  if (!res.ok) throw new Error("Failed to fetch records");
  return res.json();
};

export function UserRecords() {
  const navigate = useNavigate();
  const { data: records, isLoading, error } = useQuery({
    queryKey: ["userRecords"],
    queryFn: fetchUserRecords,
  });

  if (isLoading) return <Loader />;
  if (error)
    return (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>Could not load records.</AlertDescription>
      </Alert>
    );

  if (!records.length) {
    return <div className="p-4 text-center text-muted-foreground">No records found.</div>;
  }

  // Format date for display (same as LatestRecords)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Responsive layout: Card for mobile, Table for md+
  return (
    <div className="mx-auto pb-6 max-w-5xl">
      <div className="flex justify-between items-center mb-6">
        <BackButton />
        <h1 className="text-lg md:text-2xl font-bold">Records</h1>
        <div className="size-[36px]"></div>
      </div>
      <p className="text-muted-foreground mb-4">Records from all organizations you are a member of</p>
      {/* Card layout for mobile */}
      <div className="block md:hidden space-y-4">
        {records && records.length > 0 ? (
          records.map((record: UserRecord) => (
            <div
              key={record.id}
              className="rounded-lg shadow bg-white/90 p-4 flex flex-col gap-2 cursor-pointer"
              onClick={() => navigate(`/app/org/${record.orgId}/record/${record.id}`)}
            >
              <div>
                <div className="font-medium text-lg">{record.englishName || 'Unknown Species'}</div>
                <div className="text-sm italic text-muted-foreground">{record.scientificName}</div>
              </div>
              <div>
                {record.orgId ? (
                  <Link to={`/app/org/${record.orgId}`} onClick={e => e.stopPropagation()}>
                    <span className="inline-block bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium cursor-pointer">{record.orgName || 'Unknown'}</span>
                  </Link>
                ) : (
                  <span className="inline-block bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium">{record.orgName || 'Unknown'}</span>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{record.note || '-'}</p>
                {record.locationNote && (
                  <p className="text-sm text-muted-foreground mt-1">{record.locationNote}</p>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-2">{formatDate(record.dtm)}</div>
            </div>
          ))
        ) : (
          <div className="text-center py-4">No records found</div>
        )}
      </div>
      {/* Table layout for md+ */}
      <div className="hidden md:block overflow-auto max-h-[80vh]">
        {records && records.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organisation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.map((record: UserRecord) => (
                <tr key={record.id} onClick={() => navigate(`/app/org/${record.orgId}/record/${record.id}`)} className="cursor-pointer hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium">{record.englishName || 'Unknown Species'}</div>
                    <div className="text-sm italic text-muted-foreground">{record.scientificName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {record.orgId ? (
                      <Link to={`/app/org/${record.orgId}`} onClick={e => e.stopPropagation()}>
                        <span className="inline-block bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium cursor-pointer">{record.orgName || 'Unknown'}</span>
                      </Link>
                    ) : (
                      <span className="inline-block bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium">{record.orgName || 'Unknown'}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="text-sm text-muted-foreground line-clamp-2">{record.note || '-'}</p>
                    {record.locationNote && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{record.locationNote}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatDate(record.dtm)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-4">No records found</div>
        )}
      </div>
    </div>
  );
}

